import { getPageNumber } from '../shared/dom-pages.js';
import {
	STEP_LIST_CLASS,
	STEP_NAV_CLASS,
} from './sidebar-constants.js';

/**
* Responsibility:
* - Resolve configurator form element from runtime config.
*
* Touches:
* - Reads `config.formId` and queries `#gform_{id}`.
*
* Guarantees:
* - Returns matching form element or `null` when unavailable.
*/
export function getForm(config) {
	return document.getElementById(`gform_${config.formId}`);
}

/**
* Responsibility:
* - Resolve sidebar steps list mount element.
*
* Touches:
* - Reads `.sd-configurator__steps-nav` and nested `.sd-configurator__steps`.
*
* Guarantees:
* - Returns list element only when both nav and list mounts are present.
*/
export function getStepsListElement() {
	const nav = document.querySelector(`.${STEP_NAV_CLASS}`);
	if (!(nav instanceof HTMLElement) || !nav.classList.contains(STEP_NAV_CLASS)) {
		return null;
	}

	const list = nav.querySelector(`.${STEP_LIST_CLASS}`);
	if (!(list instanceof HTMLElement) || !list.classList.contains(STEP_LIST_CLASS)) {
		return null;
	}

	return list;
}

/**
* Responsibility:
* - Parse positive integer from mixed values.
*
* Touches:
* - Used by step state and click target parsing.
*
* Guarantees:
* - Returns positive integer or `0` when invalid.
*/
export function toPositiveInteger(value) {
	const parsed = Number.parseInt(String(value || ''), 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
* Responsibility:
* - Parse a non-negative GF page-field id used by page conditional-logic actions.
*
* Touches:
* - Normalizes `page.fieldId` values coming from Gravity Forms JS actions.
*
* Guarantees:
* - Returns a non-negative integer or `-1` when invalid.
*/
export function toNonNegativeInteger(value) {
	const parsed = Number.parseInt(String(value || ''), 10);
	return Number.isFinite(parsed) && parsed >= 0 ? parsed : -1;
}

/**
* Responsibility:
* - Normalize truthy HTML attribute values used by GF progress-bar markup.
*
* Touches:
* - Reads string attributes such as `data-start-at-zero`.
*
* Guarantees:
* - Returns `true` only for recognized truthy values.
*/
export function isTruthyAttributeValue(value) {
	const normalized = String(value || '').trim().toLowerCase();
	return normalized === '1' || normalized === 'true' || normalized === 'yes';
}

/**
* Responsibility:
* - Escape attribute selector values for safe query usage.
*
* Touches:
* - Used by hidden input lookup selector construction.
*
* Guarantees:
* - Returns escaped selector-safe value.
*/
export function escapeAttributeValue(value) {
	const rawValue = String(value || '');
	if (typeof CSS === 'object' && CSS !== null && typeof CSS.escape === 'function') {
		return CSS.escape(rawValue);
	}

	return rawValue.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
* Responsibility:
* - Find rendered page element for a specific page number.
*
* Touches:
* - Reads `.gform_page` nodes and parses page number.
*
* Guarantees:
* - Returns matching page element or `null`.
*/
export function findPageElement(form, pageNumber) {
	if (!(form instanceof HTMLElement) || pageNumber <= 0) {
		return null;
	}

	const pages = Array.from(form.querySelectorAll('.gform_page'));
	for (const page of pages) {
		if (getPageNumber(page) !== pageNumber) {
			continue;
		}

		return page;
	}

	return null;
}
